import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { parseSpeakerLine, splitPanelTranscriptLines } from '@/lib/panelTranscriptFormat';

/**
 * Panel Interview transcript panel — line-by-line dialogue layout matching
 * the HireIQ staff reference (icon + title + char count, one turn per line).
 */
export function PanelTranscriptCard({
  text = '',
  charCount,
  isLoading = false,
  errorMessage = null,
}) {
  const lines = useMemo(() => splitPanelTranscriptLines(text), [text]);
  const count = charCount ?? text.length;

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading transcript…</p>;
  }
  if (errorMessage) {
    return <p className="text-xs text-muted-foreground">{errorMessage}</p>;
  }

  return (
    <div
      className="rounded-xl border border-border/60 bg-secondary/40 p-4"
      data-testid="panel-transcript-card"
    >
      <div className="mb-3 flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="text-sm font-semibold text-foreground">Transcript</p>
        <Badge variant="outline" className="ml-auto shrink-0 text-xs font-normal">
          {count} chars
        </Badge>
      </div>
      <div
        className="max-h-96 overflow-y-auto pr-1"
        data-testid="panel-transcript-scroll"
      >
        <div className="space-y-0" data-testid="panel-transcript-text">
          {lines.map((line, i) => {
            const { speaker, message } = parseSpeakerLine(line);
            return (
              <p
                key={`${i}-${line.slice(0, 24)}`}
                className="text-xs leading-[1.65] text-foreground/90"
                data-testid="panel-transcript-line"
              >
                {speaker ? (
                  <>
                    <span className="font-medium text-foreground">{speaker}:</span>
                    {message ? ` ${message}` : ''}
                  </>
                ) : (
                  line
                )}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PanelTranscriptCard;
